import { useEffect, useState } from "react";

// Lightweight, dependency-free spotlight: darkens the screen, cuts a hole around
// a target element (found live via getBoundingClientRect, so it works even inside
// nested scroll containers where react-joyride mis-anchors), and shows a tooltip.
//
// Pass targetSelector=null for a centered message with a full dark overlay.
// The hole has pointer-events:none so the highlighted control stays clickable.
function GuideSpotlight({
  targetSelector,
  title,
  content,
  onNext,
  onBack,
  onSkip,
  nextLabel = "Got it",
  isMobile,
}) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!targetSelector) {
      setRect(null);
      return;
    }
    const update = () => {
      const el = document.querySelector(targetSelector);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect((prev) =>
        prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height
          ? prev
          : r
      );
    };
    // rAF-throttle: the capture-phase scroll listener fires for every scroll in
    // any container; measuring layout more than once a frame is pure waste.
    let raf = null;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        update();
      });
    };
    update();
    // Event-driven updates: capture-phase scroll catches scrolls in any nested
    // container, resize catches viewport changes. A SLOW safety interval covers
    // layout shifts that emit no scroll/resize event (async content loading above
    // the target, images/fonts settling, accordions expanding).
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const iv = setInterval(schedule, 500);
    return () => {
      clearInterval(iv);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [targetSelector]);

  // Waiting for a target to appear -> render nothing yet.
  if (targetSelector && !rect) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const TIP_W = isMobile ? Math.round(vw * 0.9) : 340;

  let tipStyle;
  if (!rect) {
    tipStyle = { top: "40%", left: "50%", transform: "translate(-50%,-50%)", width: TIP_W };
  } else {
    const spaceLeft = rect.left;
    const spaceRight = vw - rect.right;
    let top = rect.top;
    let left;
    if (spaceLeft >= TIP_W + 24) {
      left = rect.left - TIP_W - 18;
    } else if (spaceRight >= TIP_W + 24) {
      left = rect.right + 18;
    } else {
      left = Math.max(8, Math.min(rect.left, vw - TIP_W - 8));
      top = rect.bottom + 14;
    }
    top = Math.max(8, Math.min(top, vh - 220));
    tipStyle = { top, left, width: TIP_W };
  }

  // Shade: for a targeted step we build the dark layer from 4 blocking panels
  // around the hole, so ONLY the highlighted control is interactive (the rest of
  // the page can't be clicked mid-guide). Centered step = one full blocking layer.
  const shadeBg = "rgba(0,0,0,0.55)";
  const hx = rect ? rect.left - 8 : 0;
  const hy = rect ? rect.top - 8 : 0;
  const hw = rect ? rect.width + 16 : 0;
  const hh = rect ? rect.height + 16 : 0;

  return (
    <>
      {rect ? (
        <>
          <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: Math.max(0, hy), background: shadeBg, zIndex: 9998 }} />
          <div style={{ position: "fixed", top: hy + hh, left: 0, width: "100%", bottom: 0, background: shadeBg, zIndex: 9998 }} />
          <div style={{ position: "fixed", top: hy, left: 0, width: Math.max(0, hx), height: hh, background: shadeBg, zIndex: 9998 }} />
          <div style={{ position: "fixed", top: hy, left: hx + hw, right: 0, height: hh, background: shadeBg, zIndex: 9998 }} />
          {/* highlight ring around the (click-through) hole */}
          <div style={{ position: "fixed", top: hy, left: hx, width: hw, height: hh, borderRadius: 14, boxShadow: "0 0 0 2px #8c3e7c", zIndex: 9998, pointerEvents: "none" }} />
        </>
      ) : (
        <div style={{ position: "fixed", inset: 0, background: shadeBg, zIndex: 9998 }} />
      )}

      <div
        className="box montserrat"
        style={{
          position: "fixed",
          ...tipStyle,
          zIndex: 9999,
          backgroundColor: "#E3D0DF",
          textAlign: "center",
          fontWeight: 600,
          padding: "1.2rem",
          borderRadius: 12,
          boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
        }}
      >
        {title && (
          <h3 style={{ fontWeight: 800, color: "#5a2653", marginBottom: ".4rem", fontSize: "1.05rem" }}>
            {title}
          </h3>
        )}
        <p style={{ fontSize: isMobile ? "2.2vh" : ".95rem", marginBottom: "1rem", color: "#2c2c2c" }}>
          {content}
        </p>
        <div style={{ display: "flex", gap: ".5rem", justifyContent: "center" }}>
          {onSkip && (
            <button className="button is-rounded is-small" onClick={onSkip}>
              Skip
            </button>
          )}
          {onBack && (
            <button className="button is-rounded is-small" onClick={onBack}>
              Back
            </button>
          )}
          <button
            className="button is-rounded"
            style={{ backgroundColor: "#8c3e7c", color: "#fff", fontWeight: 700 }}
            onClick={onNext}
          >
            {nextLabel}
          </button>
        </div>
      </div>
    </>
  );
}

export default GuideSpotlight;
